import React, { forwardRef } from 'react'
import { Box, Checkbox, Flex, HStack, Skeleton, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { Column, useTableFit } from './MasterTable'

interface LoadingTableProps {
  columns: Column[]
  rows?: number
  minHeight?: string | number
}

const LoadingTable = forwardRef<HTMLDivElement, LoadingTableProps>(
  ({ columns, rows = 10, minHeight = '100%' }, ref) => {
    // Same measurement as MasterTable, so the skeleton never shows a table layout
    // that the loaded data then replaces with cards.
    const { containerRef, tableMinWidth, cardDisplay, tableDisplay } = useTableFit(columns)
    return (
      <Box ref={containerRef} minW={0} width="100%">
      <Box
        ref={ref}
        width="100%"
        minHeight={minHeight}
        overflowX="auto"
        overflowY="visible"
      >
        <Box display={cardDisplay}>
        <Flex direction="column" gap="10px" px="12px" py="10px">
          {Array.from({ length: Math.min(rows, 6) }).map((_, rowIndex) => (
            <Box key={rowIndex} bg="white" border="1px solid" borderColor="solid.50" borderRadius="10px" p="12px">
              <Skeleton height="16px" width="55%" borderRadius="6px" mb="12px" />
              <Skeleton height="12px" width="90%" borderRadius="6px" mb="8px" />
              <Skeleton height="12px" width="72%" borderRadius="6px" />
            </Box>
          ))}
        </Flex>
        </Box>
        <Box display={tableDisplay}>
        <Table width="100%" minWidth={`${tableMinWidth}px`} sx={{ tableLayout: 'fixed' }}>
          <Thead>
            <Tr>
              {columns.map((column) => (
                <Th
                  key={column.accessor}
                  width={column.width}
                  bg="transparent"
                  color="solid.400"
                  textTransform="none"
                  letterSpacing="normal"
                  fontSize="12px"
                  fontWeight="500"
                  borderBottomWidth="1px"
                  borderColor="solid.50"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  py="10px"
                >
                  {column.type === 'select' ? (
                    <Checkbox isDisabled />
                  ) : column.type === 'actions' ? (
                    <Skeleton height="12px" width="44px" borderRadius="6px" />
                  ) : (
                    <Skeleton height="12px" width="70%" borderRadius="6px" />
                  )}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <Tr key={rowIndex}>
                {columns.map((column) => (
                  <Td key={column.accessor} width={column.width} py="14px">
                    {column.type === 'select' ? (
                      <Checkbox isDisabled />
                    ) : column.type === 'actions' ? (
                      <HStack spacing="2" width="100%">
                        <Skeleton borderRadius="8px" height="28px" width="28px" />
                      </HStack>
                    ) : column.accessor === 'name' ? (
                      <HStack spacing="10px" width="100%">
                        <Skeleton borderRadius="full" height="36px" width="36px" flexShrink={0} />
                        <Skeleton height="14px" width="60%" borderRadius="6px" />
                      </HStack>
                    ) : (
                      <Skeleton height="14px" width={column.type === 'custom' ? '70%' : '85%'} borderRadius="6px" />
                    )}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
        </Box>
      </Box>
      </Box>
    )
  }
)

export default LoadingTable
